import type { PendoUserProperties } from "@newsela/snitch";

import type { UserClassroom } from "@/classrooms/queries/queries.types";
import type { User as UserV2 } from "@/types/models/User.interface";
import type { UserAttributes } from "@/types/models/User/v3/UserAttributes.interface";
import type { V3SchoolThroughTeacherResponse } from "@/teacher";
import type { V3SchoolThroughSchoolAdminResponse } from "@/schoolAdmin";

interface MapUserToPendoPropertiesInput {
  userV2: UserV2 | null | undefined;
  userAttributes: UserAttributes | undefined;
  isImpersonated: boolean | undefined;
  userClassrooms: UserClassroom[] | undefined;
  teacherSchools?: V3SchoolThroughTeacherResponse | undefined;
  schoolAdminSchools?: V3SchoolThroughSchoolAdminResponse | undefined;
}

// `School.address` is typed as `number | null` in lyceum's interface but the
// v2 API returns a nested Address object. Read defensively until the type is
// tightened.
interface SchoolAddress {
  state?: string;
  city?: string;
  country?: string;
  zip_code?: string;
}

// `School.districts` is not declared on the v2 interface but the API returns
// it. Read defensively.
interface SchoolDistrict {
  id?: number;
  unique_id?: string;
  name?: string;
}

const mapUserToPendoProperties = ({
  userV2,
  userAttributes,
  isImpersonated,
  userClassrooms,
  teacherSchools,
  schoolAdminSchools,
}: MapUserToPendoPropertiesInput): PendoUserProperties | null => {
  if (!userV2?.pk) return null;
  if (userV2.roles?.includes("student")) return null;

  const teacherSchoolUniqueIds = new Set<string>(
    [
      ...(teacherSchools?.map((school) => school.unique_id) ?? []),
      ...(userV2.teacher?.school?.unique_id
        ? [userV2.teacher.school.unique_id]
        : []),
    ].filter((uniqueId): uniqueId is string => typeof uniqueId === "string"),
  );

  const adminSchoolUniqueIds = new Set<string>(
    [
      ...(schoolAdminSchools?.length
        ? schoolAdminSchools.map((school) => school.unique_id)
        : userV2.school_admin?.school?.unique_id
          ? [userV2.school_admin.school.unique_id]
          : []),
      ...(userV2.principal?.school?.unique_id
        ? [userV2.principal.school.unique_id]
        : []),
    ].filter((uniqueId): uniqueId is string => typeof uniqueId === "string"),
  );

  const preferredSchoolUuid = userAttributes?.school_ids?.[0];
  const sortedSchools = [...(userV2.schools ?? [])].sort((a, b) => {
    if (a.unique_id === preferredSchoolUuid) return -1;
    if (b.unique_id === preferredSchoolUuid) return 1;
    return 0;
  });

  return {
    id: userV2.pk,
    unique_id: userV2.unique_id,

    is_authenticated:
      userV2.is_authenticated ?? userAttributes?.is_authenticated ?? false,
    is_impersonated: Boolean(isImpersonated),
    product: "lyceum",

    first_name: userV2.first_name,
    last_name: userV2.last_name,
    email: userV2.email,
    ...((userV2.state ?? userAttributes?.state)
      ? { state: (userV2.state ?? userAttributes?.state) as string }
      : {}),
    auth_type: userV2.auth_type,
    date_joined: userV2.date_joined,
    roles: userV2.roles,
    ...(userAttributes?.is_demo !== undefined
      ? { is_demo: userAttributes.is_demo }
      : {}),
    is_staff: userV2.is_staff,

    ...(userV2.teacher?.role ? { teacher: { role: userV2.teacher.role } } : {}),
    ...(userV2.district_admin?.district?.id !== undefined
      ? {
          district_admin: {
            district: { id: userV2.district_admin.district.id },
          },
        }
      : {}),
    ...(userV2.school_admin?.role
      ? { school_admin: { role: userV2.school_admin.role } }
      : {}),
    student: userV2.student,

    licenses: userV2.licenses,

    schools: sortedSchools.map((school) => {
      const address = school.address as unknown as
        | SchoolAddress
        | null
        | undefined;
      const districts = (school as unknown as { districts?: SchoolDistrict[] })
        .districts;
      const primaryDistrict = districts?.[0];
      const schoolId = (school as unknown as { id?: number }).id;

      return {
        ...(schoolId !== undefined ? { id: schoolId } : {}),
        ...((school as unknown as { pk?: number }).pk !== undefined
          ? { pk: (school as unknown as { pk?: number }).pk as number }
          : {}),
        ...(school.unique_id !== undefined
          ? { unique_id: school.unique_id }
          : {}),
        ...(school.name !== undefined ? { name: school.name } : {}),
        ...(address?.state !== undefined ? { state: address.state } : {}),
        ...(address?.city !== undefined ? { city: address.city } : {}),
        ...(address?.country !== undefined ? { country: address.country } : {}),
        ...(address?.zip_code !== undefined
          ? { zip_code: address.zip_code }
          : {}),
        ...(school.school_type !== undefined
          ? { school_type: school.school_type }
          : {}),
        ...(school.nces_school_id != null
          ? { nces_id: school.nces_school_id }
          : {}),
        ...(primaryDistrict
          ? {
              district: {
                ...(primaryDistrict.id !== undefined
                  ? { id: primaryDistrict.id }
                  : {}),
                ...(primaryDistrict.unique_id !== undefined
                  ? { unique_id: primaryDistrict.unique_id }
                  : {}),
                ...(primaryDistrict.name !== undefined
                  ? { name: primaryDistrict.name }
                  : {}),
              },
            }
          : {}),
        is_admin:
          school.unique_id !== undefined &&
          adminSchoolUniqueIds.has(school.unique_id),
        is_teacher:
          school.unique_id !== undefined &&
          teacherSchoolUniqueIds.has(school.unique_id),
      };
    }),

    grade_levels: userV2.classroom_grade_levels,

    classrooms: userClassrooms
      ? userClassrooms.map((classroom) => ({
          subjects: (classroom.subjects ?? []).map((subject) => subject.title),
          is_archived: classroom.date_archived !== null,
        }))
      : undefined,

    features: userV2.features,
  };
};

export default mapUserToPendoProperties;
